'use client';

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { getNetworkNameById } from '@/utils/networkNames';
import TutorialLink from '@/components/TutorialLink';
import { useVerification } from '@/lib/hooks/useVerification';
import {
  getVerdictColor,
  getVerdictLabel,
  getVerdictNote,
  getReasonLabel,
  getReasonType,
} from '@/lib/verification-verdict';
import { CheckCircle2, XCircle, HelpCircle, ChevronDown, ChevronRight } from 'lucide-react';


import { resolveLogoSrc } from '@/lib/media';
/**
 * Artwork on a public verification page is decorative — a broken-image icon is
 * worse than showing nothing. Certificates can carry an ipfs:// URI, an
 * API-relative path, or (from tenant seed data) a URL that no longer resolves,
 * so resolve what we can and drop out silently on failure.
 */
function CertificateArtwork({ imageURI }: { imageURI?: string | null }) {
    const [failed, setFailed] = useState(false);
    const src = resolveLogoSrc((imageURI ?? '').replace('ipfs://', 'https://ipfs.io/ipfs/'));
    if (!src || failed) return null;
    return (
        <div className="relative w-full max-w-xs overflow-hidden rounded-xl mx-auto shadow-md">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={src} alt="" className="w-full" onError={() => setFailed(true)} />
        </div>
    );
}

export default function VerifyPage() {
  const [url, setUrl] = useState('');
  const [manualContract, setManualContract] = useState('');
  const [manualTokenId, setManualTokenId] = useState('');
  const [manualNetworkId, setManualNetworkId] = useState('');
  const [manualOpen, setManualOpen] = useState(false);

  const { status, result, error, verifyByUrl, verifyManual, reset } = useVerification();
  const isLoading = status === 'loading';

  const handleUrlVerify = () => {
    if (!url.trim()) return;
    verifyByUrl(url);
  };

  const handleManualVerify = () => {
    if (!manualContract || !manualTokenId || !manualNetworkId) return;
    verifyManual(manualContract, manualTokenId, manualNetworkId);
  };

  const handleReset = () => {
    setUrl('');
    setManualContract('');
    setManualTokenId('');
    setManualNetworkId('');
    reset();
  };

  const verdictIcon = (verdict: string) => {
    switch (verdict) {
      case 'valid':
        return <CheckCircle2 className="size-6" />;
      case 'invalid':
        return <XCircle className="size-6" />;
      default:
        return <HelpCircle className="size-6" />;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold">Verify Certificate</h1>
        <TutorialLink href="tutorial-user.html#verifying-certificates" />
      </div>

      {/* URL Input */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <Label>Certificate link or short URL</Label>
          <div className="flex gap-2">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste a certificate link or short URL..."
              onKeyDown={(e) => e.key === 'Enter' && handleUrlVerify()}
              disabled={isLoading}
            />
            <Button onClick={handleUrlVerify} disabled={isLoading || !url.trim()}>
              {isLoading ? <Spinner className="size-4" /> : 'Verify'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Accepts short URLs (e.g. sm1.it/AbCd) or full verification URLs with contract address parameters.
          </p>
        </CardContent>
      </Card>

      {/* Manual Lookup (collapsed) */}
      <div className="border rounded-lg">
        <button
          type="button"
          className="flex items-center gap-2 w-full p-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => setManualOpen((v) => !v)}
        >
          {manualOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          Manual lookup (advanced)
        </button>
        {manualOpen && (
          <div className="px-3 pb-3 space-y-3">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <Label>Contract Address</Label>
                <Input
                  value={manualContract}
                  onChange={(e) => setManualContract(e.target.value)}
                  placeholder="0x..."
                  disabled={isLoading}
                />
              </div>
              <div className="md:w-28">
                <Label>Token ID</Label>
                <Input
                  value={manualTokenId}
                  onChange={(e) => setManualTokenId(e.target.value)}
                  placeholder="1"
                  disabled={isLoading}
                />
              </div>
              <div className="md:w-32">
                <Label>Network ID</Label>
                <Input
                  value={manualNetworkId}
                  onChange={(e) => setManualNetworkId(e.target.value)}
                  placeholder="24772477"
                  disabled={isLoading}
                />
              </div>
            </div>
            <Button
              onClick={handleManualVerify}
              disabled={isLoading || !manualContract || !manualTokenId || !manualNetworkId}
            >
              {isLoading ? <Spinner className="size-4" /> : 'Verify'}
            </Button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4">
          {/* Verdict Badge */}
          <div className={`rounded-lg p-4 flex items-center gap-3 ${getVerdictColor(result.verdict)}`}>
            {verdictIcon(result.verdict)}
            <div>
              <div className="text-lg font-bold">{getVerdictLabel(result.verdict)}</div>
              <div className="text-sm opacity-90">{getVerdictNote(result.verdict, result.reasons)}</div>
            </div>
          </div>

          {/* Reason Codes */}
          <div className="flex flex-wrap gap-2">
            {result.reasons.map((reason) => (
              <span
                key={reason}
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${
                  getReasonType(reason) === 'positive'
                    ? 'border-green-300 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-300 dark:bg-green-950'
                    : 'border-red-300 text-red-700 bg-red-50 dark:border-red-800 dark:text-red-300 dark:bg-red-950'
                }`}
              >
                {getReasonLabel(reason)}
              </span>
            ))}
          </div>

          {/* Certificate Details */}
          {result.certificate && (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div>
                  <h2 className="text-lg font-semibold">{result.certificate.name}</h2>
                  {result.certificate.tenant && (
                    <p className="text-sm text-muted-foreground">
                      Issued by {result.certificate.tenant.name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Competency</span>
                    <p className="font-medium">{result.certificate.competency}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status</span>
                    <p className="font-medium capitalize">{result.certificate.status}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Issued</span>
                    <p className="font-medium">
                      {new Date(result.certificate.issued).toLocaleDateString(undefined, {
                        timeZone: 'UTC',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Expiry</span>
                    <p className="font-medium">
                      {new Date(result.certificate.expiry).toLocaleDateString(undefined, {
                        timeZone: 'UTC',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                <CertificateArtwork imageURI={result.certificate.imageURI} />
              </CardContent>
            </Card>
          )}

          {/* Technical Details (collapsible) */}
          {result.certificate && (
            <TechnicalDetails
              certificate={result.certificate}
              shortUrlStatus={result.shortUrlStatus}
              originalUrl={result.originalUrl}
            />
          )}

          {/* Reset */}
          <Button variant="outline" onClick={handleReset}>
            Verify another
          </Button>
        </div>
      )}
    </div>
  );
}

function TechnicalDetails({
  certificate,
  shortUrlStatus,
  originalUrl,
}: {
  certificate: NonNullable<import('@/lib/verification-verdict').VerificationResult['certificate']>;
  shortUrlStatus?: string;
  originalUrl?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border rounded-lg">
      <button
        type="button"
        className="flex items-center gap-2 w-full p-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        Technical details
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-sm">
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <span className="text-muted-foreground">Contract</span>
            <span className="font-mono text-xs break-all">{certificate.contract}</span>

            <span className="text-muted-foreground">Token ID</span>
            <span>{certificate.tokenId}</span>

            <span className="text-muted-foreground">Network</span>
            <span>{getNetworkNameById(certificate.networkId)}</span>

            {shortUrlStatus && shortUrlStatus !== 'skipped' && (
              <>
                <span className="text-muted-foreground">Short URL</span>
                <span className={shortUrlStatus === 'authorized' ? 'text-green-600' : 'text-red-600'}>
                  {shortUrlStatus === 'authorized' ? 'Resolved' : 'Not found'}
                </span>
              </>
            )}

            {originalUrl && (
              <>
                <span className="text-muted-foreground">Resolved URL</span>
                <span className="font-mono text-xs break-all">{originalUrl}</span>
              </>
            )}
          </div>

          {certificate.explorerUrl && (
            <a
              href={certificate.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-primary hover:underline text-sm mt-1"
            >
              View on blockchain explorer
            </a>
          )}
        </div>
      )}
    </div>
  );
}
